/** Saved calendar edit. A stuck copy of this is what reopens the editor. */
const CALENDAR_EVENT_DRAFT_KEY = 'calendarEventFormDraft';

/** Press-and-hold on Refresh before the saved edit is wiped. */
const CALENDAR_REFRESH_HOLD_MS = 600;

/** Workbox runtime cache that can keep a day-old copy of calendar GETs. */
const CALENDAR_API_CACHE = 'api-cache';

/** Set when the resync hint's "don't show this again" is used. */
const CALENDAR_RESYNC_HINT_KEY = 'calendar-resync-hint-seen';

function calendarResyncHintSeen(storage) {
  try {
    return storage.getItem(CALENDAR_RESYNC_HINT_KEY) === '1';
  } catch {
    return true;
  }
}

function markCalendarResyncHintSeen(storage) {
  try {
    storage.setItem(CALENDAR_RESYNC_HINT_KEY, '1');
  } catch {
    /* storage can be unavailable */
  }
}

async function clearCalendarEventCache(cacheStorage) {
  const caches =
    cacheStorage ||
    (typeof globalThis !== 'undefined' ? globalThis.caches : null);
  if (!caches || typeof caches.open !== 'function') return 0;
  let cache;
  try {
    cache = await caches.open(CALENDAR_API_CACHE);
  } catch (error) {
    return 0;
  }
  const keys = cache && typeof cache.keys === 'function' ? await cache.keys() : [];
  const matches = (keys || []).filter((request) => {
    const url = request && request.url != null ? request.url : String(request || '');
    return url.includes('/api/calendar/');
  });
  await Promise.all(
    matches.map((request) =>
      cache.delete ? cache.delete(request) : Promise.resolve(false),
    ),
  );
  return matches.length;
}

/**
 * Calendar day as YYYY-MM-DD.
 * Strings keep the leading date, so an API timestamp does not shift a day.
 * Years before 1971 are refused: an empty iPhone date and `new Date(null)`
 * both land on 12/30/69 or 12/31/69.
 */
function calendarYmd(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return ymdFromParts(
      value.getFullYear(),
      value.getMonth() + 1,
      value.getDate(),
    );
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (!match) return null;
  return ymdFromParts(Number(match[1]), Number(match[2]), Number(match[3]));
}

function ymdFromParts(year, month, day) {
  if (!Number.isInteger(year) || year < 1971 || year > 2100) return null;
  if (!Number.isInteger(month) || month < 1 || month > 12) return null;
  if (!Number.isInteger(day) || day < 1 || day > 31) return null;
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() !== month - 1 ||
    utc.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Local midnight for a calendar day. `new Date('YYYY-MM-DD')` is UTC and shifts US days. */
function localDateFromYmd(value) {
  const ymd = calendarYmd(value);
  if (!ymd) return null;
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatCalendarDayLabel(value) {
  const date = value instanceof Date ? value : localDateFromYmd(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  if (!calendarYmd(date)) return '';
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
}

/** Move a selected day. A missing or epoch day starts from today, not 1969. */
function shiftLocalDay(selectedDate, offset) {
  const ymd = calendarYmd(selectedDate);
  const base = ymd ? localDateFromYmd(ymd) : new Date();
  const start = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  const days = Number(offset);
  start.setDate(start.getDate() + (Number.isFinite(days) ? days : 0));
  return start;
}

function calendarTimeHHMM(value) {
  const match = /^(\d{2}):(\d{2})/.exec(String(value || '').trim());
  return match ? `${match[1]}:${match[2]}` : '';
}

function eventOccursOn(event, dateStr) {
  const day = calendarYmd(dateStr);
  const start = calendarYmd(event && event.event_date);
  if (!day || !start) return false;
  const end = calendarYmd(event.end_date) || start;
  return day >= start && day <= end;
}

/** Briefing date chip `#calendar-{month}-{year}-{day}`. Month is 1–12. */
function calendarChipDate(href) {
  const match = /#calendar-(\d+)-(\d+)-(\d+)/.exec(String(href || ''));
  if (!match) return null;
  const month = Number(match[1]);
  const year = Number(match[2]);
  const day = Number(match[3]);
  const ymd = ymdFromParts(year, month, day);
  if (!ymd) return null;
  return { month, year, day, ymd };
}

/** Standalone calendar opened on that day in list view. */
function calendarListPath(date) {
  const chip =
    date && date.ymd
      ? date
      : calendarChipDate(
          typeof date === 'string'
            ? date
            : date && date.month != null
              ? `#calendar-${date.month}-${date.year}-${date.day}`
              : '',
        );
  if (!chip || !chip.ymd) return null;
  return `/calendar?month=${chip.month}&year=${chip.year}&day=${chip.day}&view=list`;
}

/**
 * List view is today through today+spanDays.
 * A briefing day outside that span widens the window so the day is included.
 */
function listWindowYmd(today, focusYmd, spanDays = 90) {
  const start = calendarYmd(today);
  if (!start) return null;
  const startDate = localDateFromYmd(start);
  const endDate = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
  );
  const span = Number(spanDays);
  endDate.setDate(endDate.getDate() + (Number.isFinite(span) ? span : 90));
  let startYmd = start;
  let endYmd = calendarYmd(endDate);
  const focus = calendarYmd(focusYmd);
  if (focus) {
    if (focus < startYmd) startYmd = focus;
    if (endYmd && focus > endYmd) endYmd = focus;
  }
  return { startYmd, endYmd, focusYmd: focus || null };
}

function eventInListWindow(event, bounds) {
  const eventYmd = calendarYmd(event && event.event_date);
  if (!eventYmd || !bounds || !bounds.startYmd || !bounds.endYmd) return false;
  return eventYmd >= bounds.startYmd && eventYmd <= bounds.endYmd;
}

/** Date groups in calendar order. A focus day with no events still gets a header. */
function groupListEventsByDate(events, focusYmd) {
  const grouped = new Map();
  (events || []).forEach((event) => {
    const dateStr = calendarYmd(event && event.event_date);
    if (!dateStr) return;
    if (!grouped.has(dateStr)) grouped.set(dateStr, []);
    grouped.get(dateStr).push(event);
  });
  const focus = calendarYmd(focusYmd);
  if (focus && !grouped.has(focus)) grouped.set(focus, []);
  return [...grouped.keys()]
    .sort()
    .map((dateStr) => ({ dateStr, events: grouped.get(dateStr) }));
}

function sameText(left, right) {
  return String(left || '').trim() === String(right || '').trim();
}

function isPersonalFlag(value) {
  return value === 1 || value === true || value === '1';
}

/** Null and role 3 both mean everyone can see the event. */
function visibilityRank(value) {
  if (value == null || value === '') return null;
  const rank = Number(value);
  if (!Number.isFinite(rank) || rank === 3) return null;
  return rank;
}

function calendarDraftMatchesEvent(form, event) {
  if (!form || !event) return false;
  const eventDate = calendarYmd(event.event_date);
  const formDate = calendarYmd(form.date);
  const eventEnd = calendarYmd(event.end_date);
  const multi = !!(eventEnd && eventDate && eventEnd !== eventDate);
  const formMulti = !!form.isMultiDay;
  return (
    sameText(form.title, event.title) &&
    sameText(form.description, event.description) &&
    sameText(form.location, event.location) &&
    sameText(form.meetingUrl, event.meeting_url) &&
    formDate === eventDate &&
    formMulti === multi &&
    (!formMulti || calendarYmd(form.endDate) === eventEnd) &&
    calendarTimeHHMM(form.startTime) === calendarTimeHHMM(event.start_time) &&
    calendarTimeHHMM(form.endTime) === calendarTimeHHMM(event.end_time) &&
    sameText(form.color || '#007bff', event.color || '#007bff') &&
    isPersonalFlag(form.personal) === isPersonalFlag(event.personal) &&
    (form.purpose || null) === (event.purpose || null) &&
    visibilityRank(form.maskForRole) === visibilityRank(event.maskForRole)
  );
}

/**
 * Whether a saved edit should reopen.
 * discard: drop it (corrupt date, deleted event, or nothing unsaved).
 * keep: leave storage alone and do not open (events failed to load).
 * restore: reopen the normalized edit.
 */
function resolveCalendarEventDraft(draft, events, options = {}) {
  if (!draft || typeof draft !== 'object' || !draft.eventForm) {
    return { action: 'discard' };
  }
  const date = calendarYmd(draft.eventForm.date);
  if (!date) return { action: 'discard' };

  const form = { ...draft.eventForm, date };
  const endDate = calendarYmd(form.endDate);
  if (form.endDate) form.endDate = endDate || date;

  const eventsLoaded = options.eventsLoaded !== false;
  const list = Array.isArray(events) ? events : [];
  const id = draft.editingEventId;
  if (id) {
    const event = list.find((row) => row && String(row.id) === String(id));
    if (!event) {
      return { action: eventsLoaded ? 'discard' : 'keep' };
    }
    if (calendarDraftMatchesEvent(form, event)) {
      return { action: 'discard' };
    }
  }

  return {
    action: 'restore',
    draft: {
      eventForm: form,
      editingEventId: id || null,
      showEventModal: !!draft.showEventModal,
      showGridEventForm: !!draft.showGridEventForm,
    },
  };
}

module.exports = {
  CALENDAR_API_CACHE,
  CALENDAR_EVENT_DRAFT_KEY,
  CALENDAR_REFRESH_HOLD_MS,
  clearCalendarEventCache,
  calendarYmd,
  localDateFromYmd,
  formatCalendarDayLabel,
  shiftLocalDay,
  calendarTimeHHMM,
  eventOccursOn,
  calendarChipDate,
  calendarListPath,
  listWindowYmd,
  eventInListWindow,
  groupListEventsByDate,
  calendarDraftMatchesEvent,
  resolveCalendarEventDraft,
  CALENDAR_RESYNC_HINT_KEY,
  calendarResyncHintSeen,
  markCalendarResyncHintSeen,
};
